import React, { useState } from 'react';
import { Upload, X, Trash2, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';

const GumroadStyleUpload = ({ contentType, onUploadSuccess }) => {
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [genre, setGenre] = useState('');
  const [author, setAuthor] = useState('');
  const [files, setFiles] = useState([]);
  const [customAttributes, setCustomAttributes] = useState([
    { name: '', value: '' }
  ]);
  const [uploading, setUploading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleCoverImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      setCoverImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeCoverImage = () => {
    setCoverImage(null);
    setCoverPreview(null);
  };

  const addCustomAttribute = () => {
    setCustomAttributes(prev => [...prev, { name: '', value: '' }]);
  };

  const updateCustomAttribute = (index, field, value) => {
    setCustomAttributes(prev => prev.map((attr, i) => 
      i === index ? { ...attr, [field]: value } : attr
    ));
  };

  const removeCustomAttribute = (index) => {
    setCustomAttributes(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!title) {
      alert('Please enter a title');
      return;
    }

    if (files.length === 0) {
      alert('Please select at least one file');
      return;
    }

    setUploading(true);

    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};

      // Step 1: Upload cover image if exists
      let coverImageUrl = '';
      if (coverImage) {
        const coverFormData = new FormData();
        coverFormData.append('cover_image', coverImage);

        const coverResponse = await fetch(`${BACKEND_URL}/api/upload/cover-image`, {
          method: 'POST',
          headers,
          body: coverFormData,
        });

        if (coverResponse.ok) {
          const coverData = await coverResponse.json();
          coverImageUrl = coverData.url;
        }
      }

      // Step 2: Create series
      const seriesData = {
        title,
        description: summary,
        genre: genre || 'Uncategorized',
        author: author || 'Unknown',
        cover_image_url: coverImageUrl,
        custom_attributes: customAttributes.filter(attr => attr.name && attr.value)
      };

      const seriesResponse = await fetch(`${BACKEND_URL}/api/cinematic/series/create`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(seriesData)
      });

      if (!seriesResponse.ok) {
        throw new Error('Failed to create series');
      }

      const { series_id } = await seriesResponse.json();

      // Step 3: Upload episodes
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const episodeFormData = new FormData();
        episodeFormData.append('file', file);
        episodeFormData.append('series_id', series_id);
        episodeFormData.append('episode_number', i + 1);
        episodeFormData.append('episode_title', `Episode ${i + 1}: ${file.name.replace(/\.[^/.]+$/, '')}`);

        await fetch(`${BACKEND_URL}/api/cinematic/series/${series_id}/episode`, {
          method: 'POST',
          headers,
          body: episodeFormData
        });
      }

      alert(`✅ Success! "${title}" uploaded with ${files.length} episode(s)`);
      
      // Reset form
      setTitle('');
      setSummary('');
      setGenre('');
      setAuthor('');
      setFiles([]);
      setCoverImage(null);
      setCoverPreview(null);
      setCustomAttributes([{ name: '', value: '' }]);

      if (onUploadSuccess) onUploadSuccess();

    } catch (error) {
      alert(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Cover Image Section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-white">Cover Image</h3>
          <a href="#" className="text-sm text-indigo-400 hover:text-indigo-300">Learn more</a>
        </div>
        <p className="text-sm text-gray-400 mb-4">
          This image appears in the library, discover, and profile pages. Your image should be square, at least 600x600px, and JPG, PNG or GIF format.
        </p>
        
        {coverPreview ? (
          <div className="relative inline-block">
            <img 
              src={coverPreview} 
              alt="Cover preview" 
              className="w-48 h-48 object-cover rounded-lg border-2 border-slate-600"
            />
            <button
              onClick={removeCoverImage}
              className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4 text-white" />
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center w-48 h-48 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-indigo-500 transition-colors bg-slate-800/30">
            <Upload className="w-8 h-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-400">Upload cover</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleCoverImageSelect}
              className="hidden"
            />
          </label>
        )}
      </div>

      {/* Product Info */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Product Info</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Title *
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter title"
              className="bg-slate-800 border-slate-600 text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Summary
            </label>
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="A lovely time listening to our beautiful story..."
              rows={3}
              className="bg-slate-800 border-slate-600 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Genre
              </label>
              <Input
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                placeholder="e.g., Fiction"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Author
              </label>
              <Input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Author name"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Episode Files */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Episode Files *</h3>
        <div className="space-y-3">
          {files.map((file, index) => (
            <div key={`file-${file.name}-${file.size}-${index}`} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex-1">
                <p className="text-white text-sm font-medium">{file.name}</p>
                <p className="text-gray-400 text-xs">
                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
              <button
                onClick={() => removeFile(index)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </div>
          ))}

          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-indigo-500 transition-colors bg-slate-800/30">
            <Upload className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-400">
              {files.length === 0 ? 'Upload episode files (MP3, MP4, etc.)' : 'Add more episodes'}
            </span>
            <input
              type="file"
              multiple
              accept="audio/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* Additional Details */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Additional Details</h3>
        <div className="space-y-3">
          {customAttributes.map((attr, index) => (
            <div key={`custom-attr-${index}-${attr.name || 'new'}`} className="grid grid-cols-12 gap-3">
              <Input
                value={attr.name}
                onChange={(e) => updateCustomAttribute(index, 'name', e.target.value)}
                placeholder="e.g., Duration"
                className="col-span-5 bg-slate-800 border-slate-600 text-white"
              />
              <Input
                value={attr.value}
                onChange={(e) => updateCustomAttribute(index, 'value', e.target.value)}
                placeholder="e.g., 140 minutes"
                className="col-span-6 bg-slate-800 border-slate-600 text-white"
              />
              <button
                onClick={() => removeCustomAttribute(index)}
                className="col-span-1 p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </div>
          ))}

          <Button
            onClick={addCustomAttribute}
            variant="outline"
            size="sm"
            className="w-full border-slate-600 text-gray-300 hover:bg-slate-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Detail
          </Button>
        </div>
      </div>

      {/* Upload Button */}
      <div className="pt-4 border-t border-slate-700">
        <Button
          onClick={handleUpload}
          disabled={uploading || !title || files.length === 0}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3"
        >
          {uploading ? 'Uploading...' : `Upload ${contentType}`}
        </Button>
      </div>
    </div>
  );
};

export default GumroadStyleUpload;
